// views/Dashboard/PainelExecutivo/sections/ceo/ui.tsx
//
// Peças da aba CEO. A régua visual é UMA só para geração e faturamento: a barra
// vai até 120% com um traço em 100%, e a cor vem da faixa do atingimento
// (`faixaDoAtingimento`, a mesma da tela de Geradoras). O Igor aprende a ler a
// barra uma vez e lê as duas tabelas.
import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import type { FaixaMeta } from '../../../../../src/lib/dashboard/painelCeo';

// ─────────────── formatos ───────────────

const num = (v: number, casas = 0) => v.toLocaleString('pt-BR', { maximumFractionDigits: casas, minimumFractionDigits: casas });

/** Para leitura de relance: R$ 8.240 · R$ 70,9 mil · R$ 412 mil · R$ 1,04 mi. */
export function brlCurto(v: number): string {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `R$ ${num(v / 1_000_000, 2)} mi`;
  if (a >= 100_000) return `R$ ${num(v / 1000)} mil`;
  if (a >= 10_000) return `R$ ${num(v / 1000, 1)} mil`;
  return `R$ ${num(v)}`;
}

/** Valor exato, para o title e para a conferência. */
export const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

export function kwhCurto(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${num(v / 1_000_000, 2)} GWh`;
  if (a >= 1000) return `${num(v / 1000, 1)} MWh`;
  return `${num(v)} kWh`;
}

export const pct = (fracao: number | null | undefined, casas = 0): string =>
  fracao == null || !Number.isFinite(fracao) ? '—' : `${num(fracao * 100, casas)}%`;

export const horas = (h: number): string => (h <= 0 ? '0 h' : h < 10 ? `${num(h, 1)} h` : `${num(h)} h`);

// ─────────────── faixa → cor ───────────────

export const COR_FAIXA: Record<FaixaMeta, { barra: string; texto: string; rotulo: string }> = {
  acima: { barra: 'bg-emerald-500', texto: 'text-emerald-600 dark:text-emerald-400', rotulo: 'Acima da meta' },
  no_alvo: { barra: 'bg-primary dark:bg-primary-light', texto: 'text-primary dark:text-primary-light', rotulo: 'Na meta' },
  abaixo: { barra: 'bg-amber-500', texto: 'text-amber-600 dark:text-amber-400', rotulo: 'Abaixo da meta' },
  critico: { barra: 'bg-rose-500', texto: 'text-rose-600 dark:text-rose-400', rotulo: 'Muito abaixo' },
  sem_meta: { barra: 'bg-gray-300 dark:bg-gray-600', texto: 'text-gray-400 dark:text-gray-500', rotulo: 'Sem meta' },
};

// ─────────────── movimento ───────────────

/**
 * `true` um quadro depois de montar: as barras saem de zero e crescem até o
 * valor uma vez, quando o dado chega. É o único movimento da aba — mostra que
 * o número é proporção, não enfeite. Quem pediu menos movimento vê a barra
 * pronta (`motion-reduce`).
 */
export function useMontado(): boolean {
  const [montado, setMontado] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMontado(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return montado;
}

export const CURVA_SAIDA = 'cubic-bezier(0.23, 1, 0.32, 1)';

/**
 * O tema escuro do app é uma classe no <html>, trocada pelo botão do topo. Os
 * gráficos do Recharts pintam por propriedade, não por classe: precisam saber
 * do tema para trocar a cor das marcas junto com o resto da tela.
 */
export function useModoEscuro(): boolean {
  const [escuro, setEscuro] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const obs = new MutationObserver(() => setEscuro(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return escuro;
}

/**
 * Troca de conteúdo no mesmo lugar (resumo ↔ gráfico): entra com opacidade e um
 * desfoque de 2px que somem em 200ms. O desfoque esconde o instante em que as
 * duas formas se sobrepõem; sem ele, a troca parece um corte.
 */
export const Surgir: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const montado = useMontado();
  return (
    <div
      className="motion-reduce:!transition-none"
      style={{
        opacity: montado ? 1 : 0,
        filter: montado ? 'none' : 'blur(2px)',
        transition: `opacity 200ms ${CURVA_SAIDA}, filter 200ms ${CURVA_SAIDA}`,
      }}
    >
      {children}
    </div>
  );
};

// ─────────────── barra de meta ───────────────

/** Escala da barra: 120% da meta. O traço fica em 100%. */
const TETO = 1.2;

interface BarraMetaProps {
  /** 0,9 = 90% da meta. */
  fracao: number | null;
  faixa: FaixaMeta;
  /** Ordem na lista, para as barras chegarem em cascata curta. */
  ordem?: number;
  className?: string;
}

/**
 * Realizado contra a meta. O preenchimento é recortado com `clip-path`, não
 * esticado com `width` nem `scaleX`: o canto arredondado fica certo em
 * qualquer tamanho e a animação não mexe no layout.
 */
export const BarraMeta: React.FC<BarraMetaProps> = ({ fracao, faixa, ordem = 0, className = '' }) => {
  const montado = useMontado();
  const alvo = fracao == null ? 0 : Math.min(Math.max(fracao, 0), TETO) / TETO;
  const visivel = montado ? alvo : 0;
  return (
    <div className={`relative h-2 rounded-full bg-gray-100 dark:bg-gray-700/70 ${className}`}>
      <div
        className={`absolute inset-0 rounded-full ${COR_FAIXA[faixa].barra} motion-reduce:!transition-none`}
        style={{
          clipPath: `inset(0 ${(1 - visivel) * 100}% 0 0 round 9999px)`,
          transition: `clip-path 700ms ${CURVA_SAIDA} ${Math.min(ordem, 12) * 35}ms`,
        }}
      />
      {fracao != null && (
        <span
          aria-hidden
          className="absolute -inset-y-1 w-px rounded-full bg-gray-900/30 dark:bg-white/40"
          style={{ left: `${100 / TETO}%` }}
        />
      )}
    </div>
  );
};

// ─────────────── moldura de bloco ───────────────

interface BlocoProps {
  titulo: string;
  subtitulo?: React.ReactNode;
  /** O número que resume o bloco, no canto do cabeçalho. */
  destaque?: React.ReactNode;
  acoes?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export const Bloco: React.FC<BlocoProps> = ({ titulo, subtitulo, destaque, acoes, children, className = '' }) => (
  <section className={`rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 ${className}`}>
    <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-5 pb-4 pt-5">
      <div className="min-w-0">
        <h2 className="text-[15px] font-bold text-gray-900 dark:text-white">{titulo}</h2>
        {subtitulo && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitulo}</p>}
        {acoes && <div className="mt-3 flex flex-wrap items-center gap-2">{acoes}</div>}
      </div>
      {destaque && <div className="sm:text-right">{destaque}</div>}
    </header>
    {children}
  </section>
);

/**
 * Número grande do canto do bloco: valor, e embaixo o que ele quer dizer.
 * Montserrat nos números de destaque, como nos KPIs da tela de Geradoras — é a
 * voz dos números da casa; o resto da aba fica em Inter.
 */
export const Destaque: React.FC<{ valor: React.ReactNode; legenda: React.ReactNode; tom?: string; title?: string }> = ({
  valor, legenda, tom = 'text-gray-900 dark:text-white', title,
}) => (
  <>
    <p title={title} className={`font-montserrat text-[28px] font-bold leading-none tracking-tight tabular-nums ${tom}`}>{valor}</p>
    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{legenda}</p>
  </>
);

/** Esqueleto de linhas enquanto o bloco carrega — a forma da tabela, não um spinner no meio. */
export const LinhasCarregando: React.FC<{ linhas?: number; rotulo: string }> = ({ linhas = 5, rotulo }) => (
  <div className="px-5 pb-5" aria-busy="true">
    <p className="mb-3 flex items-center gap-2 text-xs text-gray-400">
      <Loader2 size={13} className="animate-spin" /> {rotulo}
    </p>
    <div className="space-y-2.5">
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className="h-9 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-700/50" style={{ animationDelay: `${i * 80}ms` }} />
      ))}
    </div>
  </div>
);

export const ErroBloco: React.FC<{ mensagem: string }> = ({ mensagem }) => (
  <p className="px-5 pb-5 text-sm text-red-600 dark:text-red-400">{mensagem}</p>
);

/** Botão secundário da aba. Pressionar encolhe de leve: a tela respondeu. */
export const BotaoSecundario: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', ...props }) => (
  <button
    type="button"
    {...props}
    className={`inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-[background-color,transform] duration-150 ease-out hover:bg-gray-50 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 ${className}`}
  />
);

export const BotaoPrimario: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', ...props }) => (
  <button
    type="button"
    {...props}
    className={`inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-primary-dark active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
  />
);

// ─────────────── alternador ───────────────

interface OpcaoAlternador<T extends string> {
  id: T;
  rotulo: string;
  Icone?: React.ComponentType<{ size?: number }>;
}

/**
 * Duas ou mais formas de ver a mesma coisa. A pílula DESLIZA até a escolhida em
 * vez de trocar de cor no lugar: o movimento diz "é o mesmo dado, de outro
 * jeito". 200ms com saída forte — é controle, não vitrine.
 */
export function Alternador<T extends string>({ opcoes, valor, onChange, rotulo }: {
  opcoes: OpcaoAlternador<T>[];
  valor: T;
  onChange: (v: T) => void;
  rotulo: string;
}) {
  const i = Math.max(0, opcoes.findIndex((o) => o.id === valor));
  return (
    <div
      className="relative grid rounded-full bg-gray-100 p-1 dark:bg-gray-900/60"
      style={{ gridTemplateColumns: `repeat(${opcoes.length}, minmax(0, 1fr))` }}
      role="group"
      aria-label={rotulo}
    >
      <span
        aria-hidden
        className="absolute inset-y-1 left-1 rounded-full bg-white shadow-sm motion-reduce:!transition-none dark:bg-gray-700"
        style={{
          width: `calc((100% - 8px) / ${opcoes.length})`,
          transform: `translateX(${i * 100}%)`,
          transition: `transform 200ms ${CURVA_SAIDA}`,
        }}
      />
      {opcoes.map(({ id, rotulo: r, Icone }) => (
        <button
          key={id}
          type="button"
          aria-pressed={valor === id}
          onClick={() => onChange(id)}
          className={`relative z-10 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition-[color,transform] duration-150 ease-out active:scale-[0.97] ${
            valor === id ? 'text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100'
          }`}
        >
          {Icone && <Icone size={13} />} {r}
        </button>
      ))}
    </div>
  );
}

/** Seletor de mês de referência: ‹ SET/2026 ›. */
export const PassoMes: React.FC<{ mes: string; podeVoltar: boolean; podeAvancar: boolean; onVoltar: () => void; onAvancar: () => void; desligado?: boolean }> = ({
  mes, podeVoltar, podeAvancar, onVoltar, onAvancar, desligado,
}) => (
  <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800">
    <button type="button" onClick={onVoltar} disabled={desligado || !podeVoltar} aria-label="Mês anterior"
      className="rounded-l-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-800 disabled:opacity-40 dark:hover:bg-gray-700 dark:hover:text-gray-100">
      <ChevronLeft size={15} />
    </button>
    <span className="min-w-[76px] px-1 text-center text-xs font-semibold tabular-nums text-gray-800 dark:text-gray-100">{mes}</span>
    <button type="button" onClick={onAvancar} disabled={desligado || !podeAvancar} aria-label="Próximo mês"
      className="rounded-r-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-800 disabled:opacity-40 dark:hover:bg-gray-700 dark:hover:text-gray-100">
      <ChevronRight size={15} />
    </button>
  </div>
);

/** Fundo de célula pela faixa de atingimento — tons claros, o número dentro carrega a leitura. */
export const FUNDO_FAIXA: Record<FaixaMeta, string> = {
  acima: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  no_alvo: 'bg-lime-100 text-lime-900 dark:bg-lime-900/30 dark:text-lime-200',
  abaixo: 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200',
  critico: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  sem_meta: 'bg-gray-100 text-gray-400 dark:bg-gray-700/50 dark:text-gray-500',
};
